import type { analyticsCardType } from 'BreetAssets';
import type { CURRENCYCODETYPE, IApiResponse, IVIPStatsGraph, IVIPStatsGraphItem } from 'BreetHelpers';

export type { IVIPStatsGraphItem };

export interface IAnalyticsCard {
	label: string;
	value?: string | number;
	stats?: string | number;
	iconVariant: analyticsCardType;
}

export type AnalyticsPeriod = '6months' | '3months' | '1month' | '7days' | '1day';
export type AnalyticsTab = 'Transactions' | 'Withdrawals' | 'Conversions';

export const ANALYTICS_PERIODS: { label: string; value: AnalyticsPeriod }[] = [
	{ label: '6 Months', value: '6months' },
	{ label: '3 Months', value: '3months' },
	{ label: '30 Days', value: '1month' },
	{ label: '7 Days', value: '7days' },
	{ label: '24 Hours', value: '1day' },
];

export const ANALYTICS_CURRENCIES: CURRENCYCODETYPE[] = ['NGN', 'USD', 'GHS'];

export const ANALYTICS_TABS: AnalyticsTab[] = ['Transactions', 'Withdrawals', 'Conversions'];

export const getPeriodDateRange = (period: AnalyticsPeriod): { startDate: string; endDate: string } => {
	const now = new Date();
	const start = new Date(now);

	switch (period) {
		case '6months':
			start.setDate(start.getDate() - 180);
			break;
		case '3months':
			start.setMonth(start.getMonth() - 3);
			break;
		case '1month':
			start.setDate(start.getDate() - 30);
			break;
		case '7days':
			start.setDate(start.getDate() - 7);
			break;
		case '1day':
			start.setHours(start.getHours() - 24);
			break;
	}

	return { startDate: start.toISOString(), endDate: now.toISOString() };
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDateKey = (d: Date): string =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const buildYearChartData = (data: IVIPStatsGraphItem[], now: Date): IChartPoint[] => {
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const d = new Date(point.time);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	const windowStart = new Date(now.getFullYear(), now.getMonth() - 5, 1);
	const isMultiYear = windowStart.getFullYear() !== now.getFullYear();

	const slots: IChartPoint[] = [];
	for (let i = 5; i >= 0; i--) {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		const month = MONTHS[d.getMonth()];
		const value = dataMap.get(key) ?? 0;
		const tooltipLabel = value > 0 && isMultiYear ? `${month} '${String(d.getFullYear()).slice(2)}` : month;
		slots.push({ label: month, tooltipLabel, value });
	}

	return slots;
};

const buildThreeMonthChartData = (data: IVIPStatsGraphItem[], now: Date): IChartPoint[] => {
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const d = new Date(point.time);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	const windowStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);
	const isMultiYear = windowStart.getFullYear() !== now.getFullYear();

	const slots: IChartPoint[] = [];
	for (let i = 2; i >= 0; i--) {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		const month = MONTHS[d.getMonth()];
		const value = dataMap.get(key) ?? 0;
		const tooltipLabel = value > 0 && isMultiYear ? `${month} '${String(d.getFullYear()).slice(2)}` : month;
		slots.push({ label: month, tooltipLabel, value });
	}

	return slots;
};

const buildThirtyDayChartData = (data: IVIPStatsGraphItem[], now: Date): IChartPoint[] => {
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const key = toDateKey(new Date(point.time));
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	const windowStart = new Date(now);
	windowStart.setDate(windowStart.getDate() - 29);
	const isMultiYear = windowStart.getFullYear() !== now.getFullYear();

	const slots: IChartPoint[] = [];
	for (let i = 29; i >= 0; i--) {
		const d = new Date(now);
		d.setDate(d.getDate() - i);
		const key = toDateKey(d);
		const value = dataMap.get(key) ?? 0;
		const month = MONTHS[d.getMonth()];
		const day = d.getDate();

		const defaultLabel = isMultiYear ? `${month} ${day} '${String(d.getFullYear()).slice(2)}` : `${month} ${day}`;

		const tooltipLabel = value > 0 ? defaultLabel : `${month} ${day}`;

		slots.push({ label: `${d.getMonth() + 1}/${day}`, tooltipLabel, value });
	}

	return slots;
};

const buildOneDayChartData = (data: IVIPStatsGraphItem[], now: Date): IChartPoint[] => {
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const d = new Date(point.time);
		const key = `${toDateKey(d)}-${String(d.getHours()).padStart(2, '0')}`;
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	const formatHour = (h: number): string => {
		if (h === 0) return '12am';
		if (h === 12) return '12pm';
		return h < 12 ? `${h}am` : `${h - 12}pm`;
	};

	const slots: IChartPoint[] = [];
	for (let i = 23; i >= 0; i--) {
		const d = new Date(now);
		d.setHours(d.getHours() - i, 0, 0, 0);
		const key = `${toDateKey(d)}-${String(d.getHours()).padStart(2, '0')}`;
		const label = formatHour(d.getHours());
		slots.push({ label, tooltipLabel: label, value: dataMap.get(key) ?? 0 });
	}

	return slots;
};

const buildSevenDayChartData = (data: IVIPStatsGraphItem[], now: Date): IChartPoint[] => {
	const dataMap = new Map<string, number>();
	for (const point of data) {
		const key = toDateKey(new Date(point.time));
		dataMap.set(key, (dataMap.get(key) ?? 0) + point.volume);
	}

	const slots: IChartPoint[] = [];
	for (let i = 6; i >= 0; i--) {
		const d = new Date(now);
		d.setDate(d.getDate() - i);
		const key = toDateKey(d);
		const label = DAYS[d.getDay()];
		slots.push({ label, tooltipLabel: label, value: dataMap.get(key) ?? 0 });
	}

	return slots;
};

export const buildChartData = (data: IVIPStatsGraphItem[], period: AnalyticsPeriod): IChartPoint[] => {
	const now = new Date();

	if (period === '6months') return buildYearChartData(data, now);
	if (period === '3months') return buildThreeMonthChartData(data, now);
	if (period === '1month') return buildThirtyDayChartData(data, now);
	if (period === '7days') return buildSevenDayChartData(data, now);
	return buildOneDayChartData(data, now);
};

export const formatYAxisTick = (value: number): string => {
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
	return String(value);
};

export interface IChartPoint {
	label: string;
	tooltipLabel: string;
	value: number;
}

export interface IAnalyticsChart {
	dataLoading: boolean;
	vipStatsGraphData?: IApiResponse<IVIPStatsGraph>;
	activeTab: AnalyticsTab;
	activePeriod: AnalyticsPeriod;
	activeCurrency: CURRENCYCODETYPE;
}

export const rawChartDataObject = (
	vipStatsGraphData?: IApiResponse<IVIPStatsGraph>
): Record<AnalyticsTab, IVIPStatsGraphItem[] | undefined> => ({
	Conversions: vipStatsGraphData?.data?.conversions,
	Transactions: vipStatsGraphData?.data?.sells,
	Withdrawals: vipStatsGraphData?.data?.withdrawals,
});

export const getActivePeriod = (utcStartDate: string, utcEndDate: string): AnalyticsPeriod => {
	const diffMs = new Date(utcEndDate).getTime() - new Date(utcStartDate).getTime();
	const diffDays = diffMs / (1000 * 60 * 60 * 24);

	if (diffDays <= 3.5) return '1day';
	if (diffDays <= 18) return '7days';
	if (diffDays <= 60) return '1month';
	if (diffDays <= 135) return '3months';
	return '6months';
};

export type IAnalyticsViewOnlyType = 'transaction' | 'withdrawals' | 'conversions';

export interface IAnalytics {
	hideStats?: boolean;
	page?: boolean;
	viewOnly?: IAnalyticsViewOnlyType;
	isModal?: boolean;
}
